import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type ENRL } from "@/models";
import { type CHLD } from "@/models/chld";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { FiPlus } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import ENRLEditDialog from "./dialogs/ENRLEditDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  {
    id: "EnrollmentStartDate",
    field: "EnrollmentStartDate",
    title: "Enrollment Start Date",
  },
  {
    id: "EnrollmentEndDate",
    field: "EnrollmentEndDate",
    title: "Enrollment End Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Enrollment Start Date", key: "EnrollmentStartDate" },
  { label: "Enrollment End Date", key: "EnrollmentEndDate" },
];

type ENRLRow = ENRL & {
  PreschoolName?: string;
  ChildName?: string;
  __raw: ENRL;
};

export default function ENRLRecordsTab({
  enrlRecords,
  child,
}: {
  enrlRecords: ENRL[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [popupEnrollment, setPopupEnrollment] = useState<ENRL | undefined>(
    undefined
  );
  const [enrollmentDefaults, setEnrollmentDefaults] = useState<
    Partial<ENRL> | undefined
  >(undefined);

  const canEdit = leaContext.hasRole("ENRLEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupEnrollment(undefined);
    setEnrollmentDefaults(undefined);
  };

  const onAddButtonClick = () => {
    const defaults: Partial<ENRL> = {
      LocalChildId: child.LocalChildId ?? "",
      PreschoolCode: child.PreschoolCode ?? "",
      ProgramYear: child.ProgramYear ?? "",
      CSPPID: child.CSPPID ?? "",
    };
    setEnrollmentDefaults(defaults);
    setPopupEnrollment(undefined);
    setDialogOpen(true);
  };

  const onEditButtonClick = (record: ENRL) => {
    setEnrollmentDefaults(undefined);
    setPopupEnrollment(record);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: ENRLRow[] = useMemo(() => {
    return (enrlRecords ?? []).map((record) => ({
      ...record,
      ChildName: childName,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      EnrollmentStartDate: record.EnrollmentStartDate
        ? formatDateOnly(record.EnrollmentStartDate)
        : "",
      EnrollmentEndDate: record.EnrollmentEndDate
        ? formatDateOnly(record.EnrollmentEndDate)
        : "",
      __raw: record,
    }));
  }, [enrlRecords, leaContext?.currentOrgPreschools, childName]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export enrollment records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "enrollment-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add an enrollment record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) =>
          onEditButtonClick((event.dataItem as ENRLRow).__raw)
        }
      />

      {dialogOpen && (
        <ENRLEditDialog
          selectedEnrollment={popupEnrollment}
          enrollmentDefaults={enrollmentDefaults}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
